import React, { useContext, useRef, useState, useCallback, useEffect, useMemo } from "react";
import { View, StyleSheet, Pressable, Platform, TextInput } from "react-native";
import { useToolTip } from "@/hooks/useToolTip";
import { LinearGradient } from "expo-linear-gradient";
import { Image } from "expo-image";
import { ThemedText } from "@/components/ThemedText";
import Avatar from "@/components/Avatar";
import Badge from "@/components/ui/Badge";
import { EnumInput, EnumOption } from "@/components/ui/EnumInput";
import { useTheme } from "@/hooks/useTheme";
import { router, useGlobalSearchParams } from "expo-router";
import useToast from "@/hooks/useToast";
import { EntityEditContext } from "@/contexts/EntityEditContext";
import { PreviewIcon } from "./PreviewIcon";
import { BreadcrumbNavigation } from "./BreadcrumbNavigation";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  ActionButton,
  actionButtonStyles,
  CloseButton,
  CopyUrlButton,
  DeleteButton,
  EditButton,
  ExpandCollapseButton,
  PinButton,
  SaveCancelButtons,
} from "./HeaderActions";
import { supabase } from "@/lib/supabase-client";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { useDrawerCache } from "@/providers/DrawerCacheProvider";
import usePermissions from "@/hooks/usePermissions";
import type { PermissionEntity } from "@/types/permissions";
import { useModal } from "@/hooks/useModal";
import { titleCase } from "@/lib/helpers";
// Entity services for delete
import { projectService } from "@/services/projectService";
import { userService } from "@/services/userService";
import { clientService } from "@/services/clientService";
import { shiftsService } from "@/services/shiftService";
import { ShiftStatusType } from "@/types/db/shifts";
import { contactService } from "@/services/contactService";
import { invoiceService } from "@/services/invoiceService";
import { estimateService } from "@/services/estimateService";
import { courseService } from "@/services/courseService";
import { incidentService } from "@/services/incidentService";
import { investigationService } from "@/services/investigationService";
import { expenseService } from "@/services/expenseService";
import { applicationService } from "@/services/applicationService";
import { timecardService } from "@/services/timecardService";
// Deactivate confirmation modal
import { DeactivateUserModal } from "@/components/Modals/DeactivateUserModal";
// Cancel/Lost project confirmation modal
import { CancelProjectModal } from "@/components/Modals/CancelProjectModal";
// Delete confirmation modals
import { DeleteProjectModal } from "@/components/Modals/DeleteProjectModal";
import { DeleteUserModal } from "@/components/Modals/DeleteUserModal";
import { DeleteClientModal } from "@/components/Modals/DeleteClientModal";
import { DeleteContactModal } from "@/components/Modals/DeleteContactModal";
import { DeleteInvoiceModal } from "@/components/Modals/DeleteInvoiceModal";
import { DeleteEstimateModal } from "@/components/Modals/DeleteEstimateModal";
import { DeleteCourseModal } from "@/components/Modals/DeleteCourseModal";
import { DeleteIncidentModal } from "@/components/Modals/DeleteIncidentModal";
import { DeleteInvestigationModal } from "@/components/Modals/DeleteInvestigationModal";
import { DeleteExpenseModal } from "@/components/Modals/DeleteExpenseModal";
import { DeleteApplicationModal } from "@/components/Modals/DeleteApplicationModal";
import { DeleteTimecardModal } from "@/components/Modals/DeleteTimecardModal";

// Map entity type (singular) to permission entity (plural)
const entityTypeToPermissionKey: Record<string, PermissionEntity> = {
  user: "users",
  project: "projects",
  client: "clients",
  contact: "contacts",
  shift: "shifts",
  time_card: "time_cards",
  invoice: "invoices",
  estimate: "estimates",
  course: "courses",
  incident: "incidents",
  investigation: "investigations",
  expense: "expenses",
  application: "applications",
};

export type StatusColumn = {
  /** Tooltip label shown on hover (e.g. "Billing Status") */
  label: string;
  /** Field key in formData (e.g. "status", "billing_status") */
  key: string;
  /** Dropdown options */
  options: EnumOption[];
  /** Placeholder text for the picker */
  placeholder?: string;
  /** When true, reads/writes from formData.meta_data[key] instead of formData[key] */
  inMetaData?: boolean;
  /** Current value (for prop-based state in CreateHeader) */
  value?: string;
  /** Change handler (for prop-based state in CreateHeader) */
  onSelect?: (val: string) => void;
};

export interface PreviewHeaderProps {
  title: string;
  subtitle?: string;
  entityType?: string;
  entityId?: string;
  avatar?: {
    userId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    size?: number;
  };
  logo?: {
    clientId?: string;
    clientName?: string;
    height?: number;
  };
  badges?: {
    text: string;
    tone?: "amber" | "emerald" | "indigo";
  }[];
  /** When true, renders editable first/last name inputs in edit mode using the EntityEditContext */
  editableName?: boolean;
  /** When true, renders a single editable name input in edit mode (for projects, shifts, etc.) */
  editableSingleName?: boolean;
  /** When true, renders an editable description input below the name (for shifts, etc.) */
  editableDescription?: boolean;

  /** General-purpose status columns rendered as labeled dropdowns with tooltips */
  statusColumns?: StatusColumn[];

  /** @deprecated Use statusColumns instead */
  statusOptions?: EnumOption[];
  /** @deprecated Use statusColumns instead */
  statusKey?: string;
  /** @deprecated Use statusColumns instead */
  secondaryStatusOptions?: EnumOption[];
  /** @deprecated Use statusColumns instead */
  secondaryStatusKey?: string;
  /** @deprecated Use statusColumns instead */
  secondaryStatusPlaceholder?: string;
  /** @deprecated Use statusColumns instead */
  secondaryStatusInMetaData?: boolean;

  onClose?: () => void;
  children?: React.ReactNode;
  disableBreadcrumbs?: boolean;
}

function StatusColumnInput({
  col,
  editContext,
  toolTip,
  colors,
}: {
  col: StatusColumn;
  editContext: any;
  toolTip: ReturnType<typeof useToolTip>;
  colors: any;
}) {
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const value = col.inMetaData
    ? (editContext.formData?.meta_data as any)?.[col.key] || ""
    : editContext.formData?.[col.key] || "";

  const handleSelect = (val: string) => {
    if (col.inMetaData) {
      editContext.updateFormData({
        meta_data: { ...editContext.formData?.meta_data, [col.key]: val },
      } as any);
    } else {
      editContext.updateFormData({ [col.key]: val });
    }
  };

  const handlePointerEnter = () => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }}>{col.label}</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x: number, y: number, w: number) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x + (w || 0) / 2 - 50, y: y - 38, width: 100, height: 30 } as any);
      toolTip.setVisible(true);
    });
  };

  const handlePointerLeave = () => {
    isHovering.current = false;
    toolTip.setVisible(false);
  };

  return (
    <View ref={ref} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <EnumInput
        style={{ minWidth: 160 }}
        placeholder={col.placeholder || "Select status"}
        value={value}
        onSelect={handleSelect}
        options={col.options}
        disabled={!editContext.editing}
        compact
      />
    </View>
  );
}

export function PreviewHeader({
  title,
  subtitle,
  entityType,
  entityId,
  avatar,
  logo,
  badges,
  editableName,
  editableSingleName,
  editableDescription,
  statusColumns: statusColumnsProp,
  statusOptions,
  statusKey = "status",
  secondaryStatusOptions,
  secondaryStatusKey = "secondary_status",
  secondaryStatusPlaceholder = "Select status",
  secondaryStatusInMetaData = false,
  onClose,
  children,
  disableBreadcrumbs,
}: PreviewHeaderProps) {
  const { colors } = useTheme();
  const toast = useToast();
  const cache = useDrawerCache();
  const toolTip = useToolTip();

  // Merge legacy props into statusColumns for backward compat
  const statusColumns = useMemo(() => {
    if (statusColumnsProp) return statusColumnsProp;
    const cols: StatusColumn[] = [];
    if (statusOptions) {
      cols.push({ label: "Status", key: statusKey, options: statusOptions, placeholder: "Select status" });
    }
    if (secondaryStatusOptions) {
      cols.push({
        label: secondaryStatusPlaceholder,
        key: secondaryStatusKey,
        options: secondaryStatusOptions,
        placeholder: secondaryStatusPlaceholder,
        inMetaData: secondaryStatusInMetaData,
      });
    }
    return cols;
  }, [
    statusColumnsProp,
    statusOptions,
    statusKey,
    secondaryStatusOptions,
    secondaryStatusKey,
    secondaryStatusPlaceholder,
    secondaryStatusInMetaData,
  ]);

  const params = useGlobalSearchParams();

  const { triggerRefresh } = useRowSelectionContext();
  const permissions = usePermissions();
  const { openModal, closeModal } = useModal();

  // Check permissions for entity type
  const permissionKey = entityType ? entityTypeToPermissionKey[entityType] : null;
  const canUpdateEntity = permissionKey ? permissions[permissionKey]?.update : false;
  const canDeleteEntity = permissionKey ? permissions[permissionKey]?.delete : false;

  // Try to get edit context - it may not exist for all headers
  const editContext = useContext(EntityEditContext);

  // Avatar/Logo edit state
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [hasStoredAvatar, setHasStoredAvatar] = useState(false);
  const [logoDimensions, setLogoDimensions] = useState<{ width: number; height: number } | null>(null);
  const { id } = useGlobalSearchParams();

  // Check if avatar exists in storage
  useEffect(() => {
    let cancel = false;

    const checkAvatar = async () => {
      if (!avatar?.userId) {
        setHasStoredAvatar(false);
        return;
      }

      try {
        // Get tenant_id from user record
        const { data: userData } = await supabase
          .from("users")
          .select("tenant_id")
          .eq("id", avatar.userId)
          .maybeSingle();

        if (!userData || cancel) {
          setHasStoredAvatar(false);
          return;
        }

        const tenant_id = userData.tenant_id;

        // Check if avatar file exists
        const { data: files } = await supabase.storage.from("avatars").list(`${tenant_id}/users`, {
          search: avatar.userId,
        });

        if (!cancel) {
          setHasStoredAvatar(!!(files && files.length > 0));
        }
      } catch {
        if (!cancel) setHasStoredAvatar(false);
      }
    };

    checkAvatar();

    return () => {
      cancel = true;
    };
  }, [avatar?.userId]);

  // Fetch existing logo from storage when logo prop changes
  useEffect(() => {
    let cancel = false;

    const fetchLogo = async () => {
      if (!logo?.clientId) {
        setExistingLogoUrl(null);
        return;
      }

      try {
        // Get tenant_id from client record
        const { data: clientData } = await supabase
          .from("clients")
          .select("tenant_id")
          .eq("id", logo.clientId)
          .single();

        if (!clientData || cancel) return;

        const tenant_id = clientData.tenant_id;

        // List files in avatars bucket under tenant/clients folder that match clientId pattern
        const { data: files } = await supabase.storage.from("avatars").list(`${tenant_id}/clients`, {
          search: logo.clientId,
        });

        if (cancel) return;

        if (files && files.length > 0) {
          // Found a matching file, get a signed URL
          const file = files[0];
          const { data, error } = await supabase.storage
            .from("avatars")
            .createSignedUrl(`${tenant_id}/clients/${file.name}`, 3600); // 1 hour expiry

          if (!error && data?.signedUrl && !cancel) {
            setExistingLogoUrl(data.signedUrl);
          }
        } else {
          setExistingLogoUrl(null);
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
        if (!cancel) setExistingLogoUrl(null);
      }
    };

    fetchLogo();

    return () => {
      cancel = true;
    };
  }, [logo?.clientId]);

  const handleAvatarPick = useCallback(() => {
    if (!editContext) return;

    if (Platform.OS === "web") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
          // Validate file type
          if (!file.type.startsWith("image/")) {
            toast?.showToast({
              title: "Invalid File",
              message: "Please select an image file",
              toastType: "error",
            });
            return;
          }

          // Validate file size (5MB max)
          if (file.size > 5 * 1024 * 1024) {
            toast?.showToast({
              title: "File Too Large",
              message: "Image must be less than 5MB",
              toastType: "error",
            });
            return;
          }

          // Create preview URL
          const url = URL.createObjectURL(file);
          setAvatarPreviewUrl(url);

          // Update metadata with the file
          editContext.updateMetadata({ avatarFile: file });
        }
      };
      input.click();
    }
  }, [editContext, toast]);

  // const handleAvatarRemove = useCallback(() => {
  //   if (!editContext) return;
  //   setAvatarPreviewUrl(null);
  //   editContext.updateMetadata({ avatarFile: null });
  // }, [editContext]);

  const calculateScaledDimensions = useCallback((width: number, height: number) => {
    const maxWidth = 200;
    const maxHeight = 60;

    // Calculate scale factor based on which constraint is hit first
    const widthScale = maxWidth / width;
    const heightScale = maxHeight / height;
    const scale = Math.min(widthScale, heightScale, 1); // Don't scale up, only down

    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
    };
  }, []);

  const handleLogoPick = useCallback(() => {
    if (!editContext) return;

    if (Platform.OS === "web") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
          // Validate file type
          if (!file.type.startsWith("image/")) {
            toast?.showToast({
              title: "Invalid File",
              message: "Please select an image file",
              toastType: "error",
            });
            return;
          }

          // Validate file size (5MB max)
          if (file.size > 5 * 1024 * 1024) {
            toast?.showToast({
              title: "File Too Large",
              message: "Image must be less than 5MB",
              toastType: "error",
            });
            return;
          }

          // Create preview URL
          const url = URL.createObjectURL(file);
          setLogoPreviewUrl(url);

          // Reset dimensions so they recalculate on load
          setLogoDimensions(null);

          // Update metadata with the file
          editContext.updateMetadata({ logoFile: file });
        }
      };
      input.click();
    }
  }, [editContext, toast]);

  // const handleLogoRemove = useCallback(() => {
  //   if (!editContext) return;
  //   setLogoPreviewUrl(null);
  //   editContext.updateMetadata({ logoFile: null });
  // }, [editContext]);

  const handleEdit = () => {
    if (editContext) {
      // Use context to toggle edit state
      editContext.setEditing(!editContext.editing);
    } else {
      // Fallback to URL params for components without context
      if (params.edit === "true") {
        router.setParams({ edit: undefined });
      } else {
        router.setParams({ edit: "true" });
      }
    }
  };
  const blurhash =
    "|rF?hV%2WCj[ayj[a|j[az_NaeWBj@ayfRayfQfQM{M|azj[azf6fQfQfQIpWXofj[ayj[j[fQayWCoeoeaya}j[ayfQa{oLj?j[WVj[ayayj[fQoff7azayj[ayj[j[ayofayayayj[fQj[ayayj[ayfjj[j[ayjuayj[";

  const performSave = async () => {
    if (!editContext) return;
    await editContext.handleSave();
    cache.clearAll(); // Clear entire cache - breadcrumb entities will refetch
    if (entityType) triggerRefresh(entityType);
    // Clear preview URLs after successful save
    setAvatarPreviewUrl(null);
    setLogoPreviewUrl(null);

    // Refetch logo if it was uploaded
    if (logo?.clientId && editContext.metadata?.logoFile) {
      // Trigger a re-fetch by temporarily clearing then letting useEffect re-run
      setExistingLogoUrl(null);
      // Force a small delay to ensure the upload completed
      setTimeout(async () => {
        try {
          const { data: clientData } = await supabase
            .from("clients")
            .select("tenant_id")
            .eq("id", logo.clientId)
            .single();

          if (clientData) {
            const tenant_id = clientData.tenant_id;
            const { data: files } = await supabase.storage.from("avatars").list(`${tenant_id}/clients`, {
              search: logo.clientId,
            });

            if (files && files.length > 0) {
              const file = files[0];
              const { data, error } = await supabase.storage
                .from("avatars")
                .createSignedUrl(`${tenant_id}/clients/${file.name}`, 3600); // 1 hour expiry
              if (!error && data?.signedUrl) {
                setExistingLogoUrl(data.signedUrl);
              }
            }
          }
        } catch (error) {
          console.error("Failed to refetch logo:", error);
        }
      }, 500);
    }
  };

  const handleSave = async () => {
    if (!editContext) return;

    // Check if user status is being deactivated
    if (
      entityType === "user" &&
      editContext.formData?.status &&
      editContext.formData.status !== editContext.entityData?.status &&
      editContext.formData.status !== "ACTIVE"
    ) {
      openModal(
        <DeactivateUserModal
          count={1}
          hideDontShowAgain
          onConfirm={async () => {
            closeModal();
            await performSave();
          }}
          onCancel={closeModal}
        />,
      );
      return;
    }

    // Check if project status is being changed to CANCELED, LOST, or ON HOLD
    const projectStatusTriggers = ["CANCELED", "LOST", "ON HOLD"];
    if (
      entityType === "project" &&
      entityId &&
      editContext.formData?.status &&
      editContext.formData.status !== editContext.entityData?.status &&
      projectStatusTriggers.includes(editContext.formData.status)
    ) {
      openModal(
        <CancelProjectModal
          projectId={entityId}
          projectName={title}
          targetProjectStatus={editContext.formData.status as "CANCELED" | "LOST" | "ON HOLD"}
          onConfirm={async (shiftUpdates) => {
            closeModal();
            await performSave();
            for (const update of shiftUpdates) {
              await shiftsService.updateShift(update.shiftId, { status: update.targetStatus as ShiftStatusType });
            }
            triggerRefresh("shift");
          }}
          onCancel={closeModal}
        />,
      );
      return;
    }

    await performSave();
  };

  const handleCancel = () => {
    if (editContext) {
      editContext.handleCancel();
      // Clear preview URLs when canceling
      setAvatarPreviewUrl(null);
      setLogoPreviewUrl(null);
    }
  };

  // Delete handler - performs the actual deletion after confirmation
  const confirmDelete = async () => {
    if (!entityId || !entityType) return;

    try {
      switch (entityType) {
        case "project":
          await projectService.deleteProject(entityId);
          break;
        case "user":
          await userService.deleteUser(entityId);
          break;
        case "client":
          await clientService.deleteClient(entityId);
          break;
        case "shift":
          await shiftsService.deleteShift(entityId);
          break;
        case "contact":
          await contactService.deleteContact(entityId);
          break;
        case "invoice":
          await invoiceService.deleteInvoicesByIds([entityId]);
          break;
        case "estimate":
          await estimateService.deleteEstimatesByIds([entityId]);
          break;
        case "course":
          await courseService.deleteCoursesByIds([entityId]);
          break;
        case "incident":
          await incidentService.deleteIncident(entityId);
          break;
        case "investigation":
          await investigationService.deleteInvestigation(entityId);
          break;
        case "expense":
          await expenseService.deleteExpense(entityId);
          break;
        case "application":
          await applicationService.deleteApplicationsByIds([entityId]);
          break;
        case "time_card":
          await timecardService.deleteTimecardById(entityId);
          break;
        default:
          throw new Error(`Unknown entity type: ${entityType}`);
      }
      cache.clearAll();
      triggerRefresh(entityType);
      closeModal();
      // Clear router params to close drawer (same pattern as layout close)
      router.setParams({ panel: undefined });
      router.setParams({ id: undefined });
      router.setParams({ tab: undefined });
      router.setParams({ new: undefined });
      router.setParams({ edit: undefined });
      router.setParams({ related: undefined });
      toast.showToast({
        title: "Deleted",
        message: `${titleCase(entityType.replace("_", " "))} deleted successfully`,
        toastType: "info",
      });
    } catch (err) {
      closeModal();
      toast.showToast({
        title: "Delete Failed",
        message: "Could not delete this item",
        toastType: "error",
      });
    }
  };

  // Get the appropriate delete modal component for the entity type
  const getDeleteModal = () => {
    if (!entityType) return null;

    // Wrapper to ignore the dontShowAgain parameter - always show confirmation for single deletes
    const handleConfirm = () => confirmDelete();

    switch (entityType) {
      case "project":
        return <DeleteProjectModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "user":
        return <DeleteUserModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "client":
        return <DeleteClientModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "contact":
        return <DeleteContactModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "invoice":
        return <DeleteInvoiceModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "estimate":
        return <DeleteEstimateModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "course":
        return <DeleteCourseModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "incident":
        return <DeleteIncidentModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "investigation":
        return <DeleteInvestigationModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "expense":
        return <DeleteExpenseModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "application":
        return <DeleteApplicationModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      case "time_card":
        return <DeleteTimecardModal count={1} onConfirm={handleConfirm} onCancel={closeModal} hideDontShowAgain />;
      default:
        // For entities without a specific modal (e.g., shift), use a generic approach
        return null;
    }
  };

  // Opens the delete confirmation modal
  const handleDelete = () => {
    if (!entityId || !entityType) return;

    const modalContent = getDeleteModal();
    if (modalContent) {
      openModal(modalContent);
    }
  };

  return (
    <View style={[styles.header, { backgroundColor: colors.card }]}>
      <LinearGradient
        colors={[colors.primary, colors.secondary]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      />

      <View style={styles.headerContent}>
        {/* Icon/Avatar/Logo - spans full height */}
        {!avatar && !logo && <PreviewIcon entityType={entityType} size={40} />}

        {/* Avatar (circular) */}
        {avatar && (
          <View style={styles.avatarContainer}>
            {editContext?.editing && avatarPreviewUrl ? (
              // Show preview image when in edit mode with a selected file
              <Image
                source={{ uri: avatarPreviewUrl }}
                style={{
                  width: avatar.size || 60,
                  height: avatar.size || 60,
                  borderRadius: (avatar.size || 60) / 2,
                }}
                contentFit="cover"
              />
            ) : (
              // Show Avatar component normally
              <Avatar
                userId={avatar.userId}
                email={avatar.email}
                firstName={avatar.firstName}
                lastName={avatar.lastName}
                size={avatar.size || 60}
              />
            )}
            {editContext?.editing && hasStoredAvatar && !editContext.metadata?.deleteAvatar && (
              <View style={styles.avatarDeleteOverlay}>
                <Pressable
                  style={[styles.avatarDeleteButton, { backgroundColor: colors.dangerBorder }]}
                  onPress={() => {
                    editContext.updateMetadata({ deleteAvatar: true, avatarFile: null });
                    setAvatarPreviewUrl(null);
                    setHasStoredAvatar(false);
                  }}
                  accessibilityLabel="Delete avatar"
                >
                  <MaterialCommunityIcons name="trash-can-outline" size={14} color="#ffffff" />
                </Pressable>
              </View>
            )}
            {editContext?.editing && (
              <View style={styles.avatarEditOverlay}>
                <Pressable
                  style={[styles.avatarEditButton, { backgroundColor: colors.primary }]}
                  onPress={handleAvatarPick}
                  accessibilityLabel="Upload avatar"
                >
                  <MaterialCommunityIcons name="camera" size={16} color="#ffffff" />
                </Pressable>
              </View>
            )}
          </View>
        )}

        {/* Logo (rectangular) */}
        {logo && (
          <View style={[styles.logoContainer, logoDimensions && { width: logoDimensions.width }]}>
            {logoPreviewUrl ? (
              // Show preview logo - width scales automatically based on height
              <Image
                source={{ uri: logoPreviewUrl }}
                placeholder={{ blurhash }}
                style={logoDimensions || { width: 200, height: 60 }}
                contentFit="contain"
                onLoad={(e) => {
                  const { width, height } = e.source;
                  if (width && height) {
                    const scaled = calculateScaledDimensions(width, height);
                    setLogoDimensions(scaled);
                  }
                }}
              />
            ) : existingLogoUrl ? (
              // Show existing logo from storage
              <Image
                source={{ uri: existingLogoUrl }}
                placeholder={{ blurhash }}
                style={logoDimensions || { width: 200, height: 60 }}
                contentFit="contain"
                onLoad={(e) => {
                  const { width, height } = e.source;
                  if (width && height) {
                    const scaled = calculateScaledDimensions(width, height);
                    setLogoDimensions(scaled);
                  }
                }}
              />
            ) : (
              // Show placeholder when no logo exists
              <View
                style={[
                  styles.logoPlaceholder,
                  {
                    height: logo.height || 60,
                    backgroundColor: colors.backgroundSecondary,
                    borderColor: colors.border,
                  },
                ]}
              >
                <MaterialCommunityIcons name="office-building" size={40} color={colors.textSecondary} />
              </View>
            )}
            {editContext?.editing && existingLogoUrl && !editContext.metadata?.deleteLogo && (
              <View style={styles.logoDeleteOverlay}>
                <Pressable
                  style={[styles.logoDeleteButton, { backgroundColor: colors.dangerBorder }]}
                  onPress={() => {
                    editContext.updateMetadata({ deleteLogo: true, logoFile: null });
                    setLogoPreviewUrl(null);
                    setExistingLogoUrl(null);
                  }}
                  accessibilityLabel="Delete logo"
                >
                  <MaterialCommunityIcons name="trash-can-outline" size={14} color="#ffffff" />
                </Pressable>
              </View>
            )}
            {editContext?.editing && (
              <View style={styles.logoEditOverlay}>
                <Pressable
                  style={[styles.logoEditButton, { backgroundColor: colors.primary }]}
                  onPress={handleLogoPick}
                  accessibilityLabel="Upload logo"
                >
                  <MaterialCommunityIcons name="camera" size={16} color="#ffffff" />
                </Pressable>
              </View>
            )}
          </View>
        )}

        {/* Content column: top row (title, status, buttons) + description row */}
        <View style={styles.contentColumn}>
          {/* Top row: title/badges, status, buttons */}
          <View style={styles.topRow}>
            <View style={styles.headerInfo}>
              {editContext?.editing && editableName ? (
                <View style={styles.editableTitleRow}>
                  <TextInput
                    testID="preview-header-first-name"
                    style={[
                      styles.nameInput,
                      { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
                    ]}
                    value={editContext.formData?.first_name || ""}
                    onChangeText={(value) => editContext.updateFormData({ first_name: value })}
                    placeholder="First name"
                    placeholderTextColor={colors.placeholder}
                  />
                  <TextInput
                    testID="preview-header-last-name"
                    style={[
                      styles.nameInput,
                      { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
                    ]}
                    value={editContext.formData?.last_name || ""}
                    onChangeText={(value) => editContext.updateFormData({ last_name: value })}
                    placeholder="Last name"
                    placeholderTextColor={colors.placeholder}
                  />
                </View>
              ) : editContext?.editing && editableSingleName ? (
                <TextInput
                  testID="preview-header-name"
                  style={[
                    styles.singleNameInput,
                    { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
                  ]}
                  value={editContext.formData?.name || ""}
                  onChangeText={(value) => editContext.updateFormData({ name: value })}
                  placeholder="Name"
                  placeholderTextColor={colors.placeholder}
                />
              ) : (
                <View style={{ gap: 8, flexDirection: "row", alignItems: "baseline" }}>
                  <ThemedText type="title">{title}</ThemedText>
                  {typeof id === "string" ? (
                    <ThemedText
                      type="subtitle"
                      style={{ color: colors.textSecondary, fontFamily: "monospace", fontWeight: "thin" }}
                    >{`${id.slice(0, 6).toUpperCase()}`}</ThemedText>
                  ) : null}
                </View>
              )}
              {badges && (
                <View style={styles.badges}>
                  {badges.map((badge, index) => (
                    <Badge key={index} text={badge.text} tone={badge.tone} />
                  ))}
                </View>
              )}
            </View>

            {statusColumns.length > 0 &&
              editContext &&
              statusColumns.map((col) => (
                <StatusColumnInput
                  key={col.key}
                  col={col}
                  editContext={editContext}
                  toolTip={toolTip}
                  colors={colors}
                />
              ))}

            <View style={styles.headerActions}>
              {editContext ? (
                // Context-based edit actions
                editContext.editing ? (
                  <SaveCancelButtons
                    onSave={handleSave}
                    onCancel={handleCancel}
                    dirty={editContext.dirty}
                    saving={editContext.saving}
                  />
                ) : (
                  <EditButton onEdit={handleEdit} canEdit={canUpdateEntity} />
                )
              ) : (
                // Fallback to URL param-based edit button
                canUpdateEntity && (
                  <ActionButton
                    icon={params.edit === "true" ? "close" : "pencil"}
                    onPress={handleEdit}
                    tooltip={params.edit === "true" ? "Cancel" : "Edit"}
                    style={actionButtonStyles.iconBtn}
                  />
                )
              )}

              <DeleteButton onDelete={handleDelete} canDelete={canDeleteEntity && !editContext?.editing} />
              <CopyUrlButton entityType={entityType} entityId={entityId} />
              <ExpandCollapseButton entityType={entityType} entityId={entityId} />
              <PinButton />
              {onClose && <CloseButton onClose={onClose} />}
            </View>
          </View>

          {/* Description row - underneath title/status/buttons */}
          {(editableDescription || subtitle) && (
            <View style={styles.descriptionRow}>
              {editContext?.editing && editableDescription ? (
                <TextInput
                  testID="preview-header-description"
                  style={[
                    styles.descriptionInput,
                    { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
                  ]}
                  value={editContext.formData?.description || ""}
                  onChangeText={(value) => editContext.updateFormData({ description: value })}
                  placeholder="Description"
                  placeholderTextColor={colors.placeholder}
                />
              ) : (
                subtitle && <ThemedText style={{ opacity: 0.8 }}>{subtitle}</ThemedText>
              )}
            </View>
          )}
        </View>
      </View>

      {!disableBreadcrumbs && (
        <BreadcrumbNavigation currentTitle={title} currentPanel={entityType} currentId={entityId} />
      )}
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  headerGradient: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "100%",
    opacity: 0.2,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 16,
    gap: 16,
  },
  contentColumn: {
    flex: 1,
    gap: 8,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  headerInfo: {
    gap: 4,
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  descriptionRow: {
    // Description spans full width under title/status/buttons
  },
  badges: {
    flexDirection: "row",
    gap: 6,
    marginTop: 6,
  },
  avatarContainer: {
    position: "relative",
    width: 60,
    height: 60,
  },
  avatarPreview: {
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
  },
  avatarEditOverlay: {
    position: "absolute",
    bottom: -2,
    right: -2,
  },
  avatarEditButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 2px 3.84px rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  avatarDeleteOverlay: {
    position: "absolute",
    bottom: -2,
    left: -2,
  },
  avatarDeleteButton: {
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 2px 3.84px rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  logoContainer: {
    height: 60,
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  logoPlaceholder: {
    minWidth: 100,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderRadius: 8,
  },
  logoEditOverlay: {
    position: "absolute",
    bottom: -2,
    right: -2,
  },
  logoEditButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 2px 3.84px rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  logoDeleteOverlay: {
    position: "absolute",
    bottom: -2,
    left: -2,
  },
  logoDeleteButton: {
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 2px 3.84px rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  editableTitleRow: {
    flexDirection: "row",
    gap: 8,
  },
  nameInput: {
    flex: 1,
    height: 36,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 18,
    fontWeight: "600",
  },
  singleNameInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 18,
    fontWeight: "600",
    minWidth: 200,
  },
  descriptionInput: {
    height: 32,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 14,
    minWidth: 200,
  },
});
